import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppstoreAddOutlined, SearchOutlined } from "@ant-design/icons";
import SupplierList from "./SupplierList";

const Supplier = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [localSearchValue, setLocalSearchValue] = useState(""); // Local input value
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const debounceTimer = useRef(null);

  // Lấy các tham số từ URL khi component mount
  useEffect(() => {
    const search = params.get("search") || "";
    const sort = params.get("sortBy") || "name";
    const order = params.get("sortOrder") || "asc";
    const page = parseInt(params.get("page")) || 1;

    setSearchKeyword(search);
    setLocalSearchValue(search);
    setSortBy(sort);
    setSortOrder(order);
    setCurrentPage(page);
  }, [location.search]);

  // Debounce search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (localSearchValue !== searchKeyword) {
        setSearchKeyword(localSearchValue);
        setCurrentPage(1);
        updateURL(localSearchValue, sortBy, sortOrder, 1);
      }
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearchValue, sortBy, sortOrder]);

  const handleSearchKeywordChange = (event) => {
    const value = event.target.value;
    setLocalSearchValue(value);
  };

  const handleSortByChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    updateURL(searchKeyword, newSortBy, sortOrder, 1);
  };

  const handleSortOrderChange = (newOrder) => {
    setSortOrder(newOrder);
    setCurrentPage(1);
    updateURL(searchKeyword, sortBy, newOrder, 1);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchKeyword(localSearchValue);
    setCurrentPage(1);
    updateURL(localSearchValue, sortBy, sortOrder, 1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateURL(searchKeyword, sortBy, sortOrder, newPage);
  };

  const updateURL = (search, sortByParam, sortOrderParam, newPage = 1) => {
    const params = new URLSearchParams();
    params.set("page", newPage.toString());

    if (search.trim()) params.set("search", search.trim());
    if (sortByParam !== "name") params.set("sortBy", sortByParam);
    if (sortOrderParam !== "asc") params.set("sortOrder", sortOrderParam);

    window.history.replaceState({}, "", `/admin/supplier?${params.toString()}`);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setLocalSearchValue("");
    setSortBy("name");
    setSortOrder("asc");
    setCurrentPage(1);
    window.history.replaceState({}, "", "/admin/supplier?page=1");
  };

  const sortOptions = [
    { value: "name", label: "Tên nhà cung cấp" },
    { value: "id", label: "Mã nhà cung cấp" },
  ];

  return (
    <div className="bg-gray-50 h-screen overflow-y-auto">
      <div className="min-h-screen overflow-y-auto">
        <div className="p-1 space-y-2">
          {/* Header + Filters Combined */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Quản lý nhà cung cấp
                </h1>
                <p className="text-gray-600 mt-1">
                  Quản lý thông tin các nhà cung cấp và thương hiệu
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/admin/supplier/create"
                  className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200 shadow-sm"
                >
                  <AppstoreAddOutlined />
                  Thêm nhà cung cấp
                </Link>
              </div>
            </div>

            {/* Filters */}
            <div className="border-t border-gray-200 pt-4">
              <form onSubmit={handleSearchSubmit} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Search Input */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <SearchOutlined className="mr-1" />
                      Tìm kiếm
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Tên NCC, mã NCC..."
                        value={localSearchValue}
                        onChange={handleSearchKeywordChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                      />

                      {localSearchValue !== searchKeyword && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sort Options */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sắp xếp theo
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={sortBy}
                        onChange={(e) => handleSortByChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={sortOrder}
                        onChange={(e) => handleSortOrderChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="asc">Tăng dần</option>
                        <option value="desc">Giảm dần</option>
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="lg:col-span-1 flex items-end gap-2 flex flex-row items-center justify-center pt-2">
                    <button
                      type="submit"
                      className="flex-1 py-0.5 inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                    >
                      <SearchOutlined />
                      Tìm kiếm
                    </button>

                    <button
                      type="button"
                      onClick={clearFilters}
                      className="flex-1 py-0.5 inline-flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                    >
                      Xóa bộ lọc
                    </button>
                  </div>
                </div>
              </form>

              {/* Active Filters Display */}
              {(searchKeyword || sortBy !== "name" || sortOrder !== "asc") && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      Bộ lọc hiện tại:
                    </span>

                    {searchKeyword && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Tìm kiếm: "{searchKeyword}"
                      </span>
                    )}

                    {sortBy !== "name" && (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Sắp xếp:{" "}
                        {sortOptions.find((opt) => opt.value === sortBy)?.label}
                      </span>
                    )}

                    {sortOrder !== "asc" && (
                      <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Thứ tự: Giảm dần
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Supplier List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <SupplierList
              searchKeyword={searchKeyword}
              sortBy={sortBy}
              sortOrder={sortOrder}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Supplier;
